import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AnimalType } from '../lib/constants'
import {
  enableReportNotifications,
  notificationsSupported,
} from '../lib/pushNotifications'

interface Props {
  kind: 'sighting' | 'lost'
  reportId: number
  animalType: AnimalType
  onDone: () => void
  onSkip: () => void
}

export function NotificationOptInPrompt({
  kind,
  reportId,
  animalType,
  onDone,
  onSkip,
}: Props) {
  const { t } = useTranslation()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const animalLabel = t(`types.${animalType}`).toLowerCase()

  async function handleEnable() {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      if (!notificationsSupported()) {
        throw new Error(t('notify.unsupported'))
      }
      await enableReportNotifications({ kind, id: reportId })
      onDone()
    } catch (caught) {
      setError(
        caught instanceof Error && caught.message
          ? caught.message
          : t('notify.enableFailed'),
      )
      setBusy(false)
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={busy ? undefined : onSkip}
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="notify-opt-in-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="notify-opt-in-title">{t('notify.title')}</h2>
        <p>
          {t('notify.ask', {
            animal: animalLabel,
            context:
              kind === 'lost' ? t('notify.contextLost') : t('notify.contextSighting'),
          })}
        </p>
        <p className="field-help">{t('notify.scope')}</p>
        {error && (
          <p className="field-error" role="alert">
            {error}
          </p>
        )}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onSkip}
            disabled={busy}
          >
            {t('notify.notNow')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void handleEnable()}
            disabled={busy}
          >
            {busy ? t('notify.enabling') : t('notify.enable')}
          </button>
        </div>
      </div>
    </div>
  )
}
